/**
 * Activitati practice (B3): regulile pure ale cardului si ale depunerii dovezilor, fara React.
 */
import type { SlideDto } from '@/api/generated/types.gen';
import type { PickedPhoto } from '@/components/mobile/PhotoPicker';
import { pentruServer } from '../../utils/clase-canonice';

/**
 * Plafonul de poze pe calea profesorului. Oglinda lui DepunereActivitateService.MIN_POZE/MAX_POZE
 * din backend: serverul refuza exact ce refuza si ecranul. Nu e DEFAULT_LIMITS din
 * domains/lessons/shared/constants.ts (acela ramane 4, pentru playerul de lectie).
 */
export const PRACTICAL_EVIDENCE_LIMITS = { MIN: 1, MAX: 5 } as const;

/**
 * Tipurile care se depun prin ecranul de dovezi (fara parcursul de lectie): activitatea practica (B3)
 * si provocarea pentru scoala (2026-10-02). Tot ce difera intre ele sta AICI, intr-un singur loc.
 */
export type TipDepunere = 'PRACTICAL_ACTIVITY' | 'SCHOOL_CHALLENGE';

export interface ConfigDepunere {
  tip: TipDepunere;
  /** Lista din care se intra si la care duce „Înapoi". */
  lista: string;
  /** Activitatea practica se puncteaza pe clase; provocarea e a intregii scoli. */
  cereClase: boolean;
  /** La provocare descrierea e obligatorie: acolo spune scoala cate clase au participat. */
  cereDescriere: boolean;
  /** O singura depunere activa per scoala (provocare) sau oricate (activitate practica). */
  oSinguraDepunere: boolean;
  texte: {
    butonIncarca: string;
    negasita: string;
    neincarcata: string;
    seIncarca: string;
    termenExpirat: string;
    titluIncarcare: string;
    titluTrimis: string;
    notaTrimis: string;
    inapoiLaLista: string;
    etichetaDescriere: string;
    indicatieDescriere: string;
  };
}

export const CONFIG_DEPUNERE: Record<TipDepunere, ConfigDepunere> = {
  PRACTICAL_ACTIVITY: {
    tip: 'PRACTICAL_ACTIVITY',
    lista: '/activitati-practice',
    cereClase: true,
    cereDescriere: false,
    oSinguraDepunere: false,
    texte: {
      butonIncarca: 'Încarcă poze',
      negasita: 'Activitatea nu a fost găsită.',
      neincarcata: 'Activitatea nu a putut fi încărcată.',
      seIncarca: 'Se încarcă activitatea',
      termenExpirat: 'Termenul acestei activități a expirat.',
      titluIncarcare: 'Încarcă dovezile pentru această activitate',
      titluTrimis: 'Activitate trimisă',
      notaTrimis: 'Echipa ECOTIC verifică dovezile. Starea o vezi pe cardul activității și în Raport activități.',
      inapoiLaLista: 'Înapoi la activități',
      etichetaDescriere: 'Descriere (opțional)',
      indicatieDescriere: 'Ce ați făcut, câți elevi au participat…',
    },
  },
  SCHOOL_CHALLENGE: {
    tip: 'SCHOOL_CHALLENGE',
    lista: '/provocari-scoli',
    cereClase: false,
    cereDescriere: true,
    oSinguraDepunere: true,
    texte: {
      butonIncarca: 'Încarcă dovezi',
      negasita: 'Provocarea nu a fost găsită.',
      neincarcata: 'Provocarea nu a putut fi încărcată.',
      seIncarca: 'Se încarcă provocarea',
      termenExpirat: 'Termenul acestei provocări a expirat.',
      titluIncarcare: 'Încarcă dovezile pentru această provocare',
      titluTrimis: 'Dovezi trimise',
      notaTrimis:
        'Echipa ECOTIC verifică dovezile. Punctele se acordă după validare; starea o vezi pe cardul provocării.',
      inapoiLaLista: 'Înapoi la provocări',
      etichetaDescriere: 'Cum s-a desfășurat provocarea în școală?',
      indicatieDescriere: 'Câte clase au participat, cum ați promovat mesajul în școală…',
    },
  },
};

/** Configul tipului, sau `undefined` pentru un tip care nu se depune prin ecranul de dovezi. */
export function configDepunere(lessonType: string | undefined | null): ConfigDepunere | undefined {
  return lessonType === 'PRACTICAL_ACTIVITY' || lessonType === 'SCHOOL_CHALLENGE'
    ? CONFIG_DEPUNERE[lessonType]
    : undefined;
}

/**
 * Cardul cu „Încarcă poze" + „Detalii" il vede doar profesorul, pe activitati practice si pe
 * provocarile pentru scoli. ADMIN/DBEST n-au scoala in JWT, deci n-au ce depune (decis 24.09).
 */
export function usesPracticalActions(lessonType: string | undefined, role: string | undefined): boolean {
  return configDepunere(lessonType) !== undefined && role === 'PROFESOR';
}

/** In romana, de la 20 in sus numeralul cere „de" inaintea substantivului: „20 de ori". */
function cuDe(n: number): string {
  const rest = n % 100;
  return n >= 20 && !(rest >= 1 && rest <= 19) ? `${n} de` : `${n}`;
}

/**
 * Linia de stare de sub descrierea cardului, din contoarele calculate de backend.
 * Ex.: 1 in asteptare + 2 aprobate -> „Trimisă de 3 ori · 1 în așteptare · 2 aprobate".
 * Fara depuneri -> null (linia nu se afiseaza).
 */
export function stareDepuneriText(inAsteptare?: number | null, aprobate?: number | null): string | null {
  const p = inAsteptare ?? 0;
  const a = aprobate ?? 0;
  const total = p + a;
  if (total <= 0) return null;

  const parti = [total === 1 ? 'Trimisă o dată' : `Trimisă de ${cuDe(total)} ori`];
  if (p > 0) parti.push(`${p} în așteptare`);
  if (a > 0) parti.push(a === 1 ? '1 aprobată' : `${cuDe(a)} aprobate`);
  return parti.join(' · ');
}

/**
 * Starea provocarii pentru scoala, din contoarele calculate de backend PE SCOALA (nu pe profesor).
 * O provocare are cel mult o depunere activa, deci nu se numara „de N ori".
 */
export function stareProvocareText(inAsteptare?: number | null, aprobate?: number | null): string | null {
  if ((aprobate ?? 0) > 0) return 'Dovezi aprobate';
  if ((inAsteptare ?? 0) > 0) return 'Dovezi trimise · în așteptare';
  return null;
}

/** Scoala are deja o depunere in asteptare sau aprobata: nu mai poate trimite alta. */
export function provocareDejaDepusa(inAsteptare?: number | null, aprobate?: number | null): boolean {
  return (inAsteptare ?? 0) + (aprobate ?? 0) > 0;
}

/** „1 noiembrie" din termenul lectiei (string ISO fara zona); `null` daca lipseste sau nu se citeste. */
export function ziSiLuna(deadline: string | Date | null | undefined): string | null {
  if (!deadline) return null;
  const d = new Date(deadline);
  if (!Number.isFinite(d.getTime())) return null;
  return new Intl.DateTimeFormat('ro-RO', { day: 'numeric', month: 'long' }).format(d);
}

/** „1000 de puncte"; `null` fara punctaj. `punctajMaxim` vine ca numar (tipul generat spune bigint). */
export function puncteText(punctajMaxim: number | bigint | null | undefined): string | null {
  const n = Number(punctajMaxim ?? 0);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n === 1 ? '1 punct' : `${cuDe(n)} puncte`;
}

/**
 * Linia de sub titlul provocarii: „1000 de puncte · dovezi până pe 1 noiembrie". Fiecare jumatate apare
 * doar daca are valoare; fara niciuna -> null (linia nu se afiseaza).
 */
export function rezumatProvocare(
  punctajMaxim: number | bigint | null | undefined,
  deadline: string | Date | null | undefined
): string | null {
  const puncte = puncteText(punctajMaxim);
  const zi = ziSiLuna(deadline);
  const parti = [puncte, zi ? `dovezi până pe ${zi}` : null].filter(Boolean);
  return parti.length ? parti.join(' · ') : null;
}

/**
 * Rutele „Detalii" si „Încarcă" (routes-provider: PRACTICAL_ACTIVITY_* / SCHOOL_CHALLENGE_*). Un tip
 * necunoscut cade pe activitatea practica, ca inainte.
 */
export function practicalPaths(id: string, lessonType?: string): { detalii: string; incarca: string } {
  const lista = (configDepunere(lessonType) ?? CONFIG_DEPUNERE.PRACTICAL_ACTIVITY).lista;
  return {
    detalii: `${lista}/id/${id}/detalii`,
    incarca: `${lista}/id/${id}/incarca`,
  };
}

/** O sectiune de pe pagina „Detalii", dintr-un slide INFO. */
export interface InfoSection {
  key: string;
  title: string;
  content: string;
  image?: string;
}

/**
 * Sectiunile paginii „Detalii": doar slide-urile INFO, in ordinea `number` (fara numar → la coada,
 * in ordinea primita). Backendul n-are metadate pe slide (confirmat 24.09): titlul e `text`,
 * continutul (markdown) e `description`, imaginea sta mereu deasupra textului. Sectiunile fara
 * titlu, continut si imagine nu produc nimic pe ecran, deci se omit.
 */
export function infoSlidesForDetails(slides: SlideDto[] | undefined | null): InfoSection[] {
  if (!slides) return [];
  return slides
    .map((slide, index) => ({ slide, index }))
    .filter(({ slide }) => slide.type === 'INFO')
    .sort((a, b) => (a.slide.number ?? Infinity) - (b.slide.number ?? Infinity) || a.index - b.index)
    .map(({ slide, index }) => ({
      key: slide.id ?? `info-${index}`,
      title: slide.text ?? '',
      content: slide.description ?? '',
      image: slide.image || undefined,
    }))
    .filter((s) => s.title.trim() || s.content.trim() || s.image);
}

/**
 * Task 15: pe calea profesorului dovada poate fi si PDF. Plafonul e al backendului
 * (`/api/files/upload`, folderul LESSON_ANSWER_PHOTOS: 15 MB, primii octeti `%PDF-`); aici doar
 * scutim omul de un upload sigur refuzat. Pozele raman pe `uploadAPI.validateImage`.
 */
export const PDF_DOVADA_MAX_MB = 15;

export function esteFisierPdf(file: { type: string }): boolean {
  return file.type === 'application/pdf';
}

export function validaPdfDovada(file: { type: string; size: number }): { valid: boolean; error?: string } {
  if (!esteFisierPdf(file)) return { valid: false, error: 'Poți încărca doar poze sau fișiere PDF.' };
  if (file.size <= 0) return { valid: false, error: 'Fișierul PDF este gol.' };
  if (file.size > PDF_DOVADA_MAX_MB * 1024 * 1024) {
    return { valid: false, error: `Fișierul PDF e prea mare. Poți încărca cel mult ${PDF_DOVADA_MAX_MB} MB.` };
  }
  return { valid: true };
}

/** Pozele urcate cu succes: doar ele pleaca in cerere (o poza esuata nu blocheaza, dar nici nu pleaca). */
function pozeUrcate(photos: PickedPhoto[]): string[] {
  return photos.filter((p) => p.status === 'done' && p.fileId).map((p) => p.fileId as string);
}

/**
 * „Trimite" e activ doar cand cererea are sanse sa treaca: nu se trimite deja (dublu-tap), nicio poza
 * nu mai e in lucru, intre MIN si MAX poze urcate si clasele completate. Plafonul e al caii
 * profesorului, oglinda serverului.
 */
export function canSubmitEvidence(
  s: { photos: PickedPhoto[]; clase: string; submitting: boolean; description?: string },
  reguli: Pick<ConfigDepunere, 'cereClase' | 'cereDescriere'> = CONFIG_DEPUNERE.PRACTICAL_ACTIVITY
): boolean {
  if (s.submitting) return false;
  if (s.photos.some((p) => p.status === 'pending' || p.status === 'uploading')) return false;
  const n = pozeUrcate(s.photos).length;
  if (n < PRACTICAL_EVIDENCE_LIMITS.MIN || n > PRACTICAL_EVIDENCE_LIMITS.MAX) return false;
  if (reguli.cereClase && s.clase.trim() === '') return false;
  if (reguli.cereDescriere && (s.description ?? '').trim() === '') return false;
  return true;
}

/** Corpul pentru POST /api/lessons/{id}/depunere-activitate. Descrierea goala nu se trimite deloc. */
export function evidenceBody(
  s: { photos: PickedPhoto[]; clase: string; description: string },
  cereClase = true
): {
  photoUrls: string[];
  clase?: string;
  description?: string;
} {
  const description = s.description.trim();
  return {
    photoUrls: pozeUrcate(s.photos),
    // Provocarea pentru scoala nu trimite clase: serverul nici nu le-ar scrie.
    ...(cereClase ? { clase: pentruServer(s.clase) } : {}),
    ...(description ? { description } : {}),
  };
}

/**
 * Termenul activitatii a trecut? `deadline` vine de la backend ca string ISO fara zona (LocalDateTime,
 * ora Romaniei), iar `new Date` il citeste ca ora locala a telefonului — pentru profesori, aceeasi.
 * Lipsa sau o valoare necitibila = fara termen (serverul ramane ultimul arbitru, cu 409).
 * Egal cu `now` = inca nu a expirat (serverul refuza doar dupa termen).
 */
export function termenExpirat(deadline: string | Date | null | undefined, now: Date = new Date()): boolean {
  if (!deadline) return false;
  const t = new Date(deadline).getTime();
  return Number.isFinite(t) && t < now.getTime();
}
